import type { Vec2 } from '../geo/types';
import { WIND_HEADING } from '../world/harbour/shipTemplates';
import { OPEN_WATER } from './openWater';

/*
 * The water's moving surface away from the painter view. The painter's water is a fit to the
 * painting and stays exactly as it is (waterCues.ts); the painting draws no waves to fit. What is
 * here is for the eye on the quay and in the boat: a few trains of waves running before the wind
 * that flies the fleet's pennants, long and low on the open IJ and its harbour, short ripples in
 * the shelter of the canals. The surface itself stays a level plane: the waves tilt the mirror the
 * sky is seen in (water.frag), and a boat rides their height (controls/boat.ts).
 *
 * Lengths and heights are estimates for a moderate breeze on sheltered water, not measurements.
 * Each train runs at the speed of a deep-water wave of its length.
 */
export interface WaveTrain {
  /** Degrees to the left of the wind that the train runs towards. */
  readonly offWindDeg: number;
  readonly lengthM: number;
  /** Height of a crest over the still water. */
  readonly amplitudeM: number;
  readonly phase: number;
}

/** Trains per kind of water; water.frag's arrays hold the open water's first, then the sheltered. */
export const WAVE_TRAINS = 4;

export const OPEN_WAVES: readonly WaveTrain[] = [
  { offWindDeg: 0, lengthM: 7.5, amplitudeM: 0.05, phase: 0 },
  { offWindDeg: 23, lengthM: 4.6, amplitudeM: 0.032, phase: 1.7 },
  { offWindDeg: -17, lengthM: 2.9, amplitudeM: 0.02, phase: 4.1 },
  { offWindDeg: 48, lengthM: 1.7, amplitudeM: 0.011, phase: 2.6 },
];

export const SHELTERED_WAVES: readonly WaveTrain[] = [
  { offWindDeg: 12, lengthM: 2.7, amplitudeM: 0.012, phase: 0.6 },
  { offWindDeg: -31, lengthM: 1.8, amplitudeM: 0.009, phase: 3.3 },
  { offWindDeg: 57, lengthM: 1.15, amplitudeM: 0.006, phase: 5.2 },
  { offWindDeg: -74, lengthM: 0.75, amplitudeM: 0.004, phase: 1.1 },
];

const GRAVITY = 9.81;

/** A train as the shader and the boat use it: its wave vector on the world's xz plane, in radians
 *  a metre, its turn in radians a second, its phase and its amplitude. */
export interface WaveVector {
  readonly kx: number;
  readonly kz: number;
  readonly omega: number;
  readonly phase: number;
  readonly amplitudeM: number;
}

export function waveVector(train: WaveTrain): WaveVector {
  const k = (2 * Math.PI) / train.lengthM;
  // WIND_HEADING turns from east towards north on the layout, whose north is the world's -z.
  const heading = WIND_HEADING + (train.offWindDeg * Math.PI) / 180;
  return {
    kx: Math.cos(heading) * k,
    kz: -Math.sin(heading) * k,
    omega: Math.sqrt(GRAVITY * k),
    phase: train.phase,
    amplitudeM: train.amplitudeM,
  };
}

const OPEN_VECTORS = OPEN_WAVES.map(waveVector);
const SHELTERED_VECTORS = SHELTERED_WAVES.map(waveVector);

/** Every train in the order of water.frag's arrays. */
export const WAVE_VECTORS: readonly WaveVector[] = [...OPEN_VECTORS, ...SHELTERED_VECTORS];

/** Whether a kind of water, by its vertex code, lies open to the wind as the IJ does. */
export function isOpenWater(kindCode: number): boolean {
  return kindCode < OPEN_WATER.kindLimit;
}

function vectorsOf(open: boolean): readonly WaveVector[] {
  return open ? OPEN_VECTORS : SHELTERED_VECTORS;
}

/** The water's height over its still level at a layout point, in metres. */
export function waveHeight(p: Vec2, open: boolean, timeSeconds: number): number {
  let height = 0;
  for (const w of vectorsOf(open))
    height += w.amplitudeM * Math.sin(w.kx * p[0] - w.kz * p[1] - w.omega * timeSeconds + w.phase);
  return height;
}

/** The water's slope on the world's xz plane at a layout point: rise per metre along x and z. */
export function waveSlope(p: Vec2, open: boolean, timeSeconds: number): Vec2 {
  let [sx, sz] = [0, 0];
  for (const w of vectorsOf(open)) {
    const c = w.amplitudeM * Math.cos(w.kx * p[0] - w.kz * p[1] - w.omega * timeSeconds + w.phase);
    sx += c * w.kx;
    sz += c * w.kz;
  }
  return [sx, sz];
}

/** The highest any crest can stand over the still water. */
export function waveCrest(open: boolean): number {
  return vectorsOf(open).reduce((sum, w) => sum + w.amplitudeM, 0);
}
